import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { Helmet } from "react-helmet";

// ── Brand tokens ───────────────────────────────────────────────
const PURPLE  = "#8c3e7c";
const PURPLE_DARK = "#6b2e5e";
const PINK_BG = "#F7F0F5";
const ORANGE  = "#ea580c";

// ── Scroll-reveal hook ─────────────────────────────────────────
function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

// ── Reveal wrapper: fade + slide up on scroll ──────────────────
function Reveal({ children, delay = 0, className = "", style = {} }) {
  const [ref, visible] = useInView();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        ...style,
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(22px)",
        transition: `opacity 0.6s ease ${delay}ms, transform 0.6s ease ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

// ── Primary CTA button ─────────────────────────────────────────
function PrimaryBtn({ label = "Start Your 14-Day Free Trial →", onClick, fullWidth = false }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        backgroundColor: ORANGE,
        color: "#fff",
        padding: "15px 40px",
        fontSize: "17px",
        fontWeight: 800,
        borderRadius: "12px",
        border: "none",
        cursor: "pointer",
        width: fullWidth ? "100%" : "auto",
        letterSpacing: "-0.01em",
        boxShadow: hover
          ? "0 10px 28px rgba(234,88,12,0.38)"
          : "0 4px 16px rgba(234,88,12,0.28)",
        transform: hover ? "translateY(-2px)" : "translateY(0)",
        transition: "all 0.22s ease",
      }}
    >
      {label}
    </button>
  );
}

// ── Section wrapper ────────────────────────────────────────────
function Section({ children, bg = "#fff", py = "80px" }) {
  return (
    <section style={{ backgroundColor: bg, padding: `${py} 0` }}>
      <div style={{ maxWidth: 1040, margin: "0 auto", padding: "0 24px" }}>
        {children}
      </div>
    </section>
  );
}

// ── Divider label ──────────────────────────────────────────────
function EyebrowLabel({ children }) {
  return (
    <p style={{
      color: PURPLE,
      fontWeight: 700,
      fontSize: "12px",
      letterSpacing: "0.12em",
      textTransform: "uppercase",
      marginBottom: "14px",
    }}>
      {children}
    </p>
  );
}

// ── Section heading ────────────────────────────────────────────
function SectionHeading({ children, light = false, align = "center" }) {
  return (
    <h2 style={{
      fontSize: "clamp(26px, 4vw, 40px)",
      fontWeight: 800,
      letterSpacing: "-0.03em",
      lineHeight: 1.15,
      color: light ? "#fff" : "#12071a",
      textAlign: align,
      marginBottom: "14px",
    }}>
      {children}
    </h2>
  );
}

// ── Sub-heading copy ───────────────────────────────────────────
function SubText({ children, light = false, align = "center" }) {
  return (
    <p style={{
      fontSize: "17px",
      lineHeight: 1.7,
      color: light ? "rgba(255,255,255,0.72)" : "#5a5068",
      textAlign: align,
      maxWidth: 560,
      margin: align === "center" ? "0 auto" : "0",
    }}>
      {children}
    </p>
  );
}

// ── Feature card ───────────────────────────────────────────────
function FeatureCard({ icon, title, desc, delay }) {
  const [hover, setHover] = useState(false);
  return (
    <Reveal delay={delay}>
      <div
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          backgroundColor: "#fff",
          border: hover ? `1.5px solid ${PURPLE}33` : "1.5px solid #f0e8ef",
          borderRadius: "16px",
          padding: "28px 26px",
          boxShadow: hover ? "0 8px 24px rgba(140,62,124,0.10)" : "0 2px 8px rgba(0,0,0,0.04)",
          transform: hover ? "translateY(-4px)" : "translateY(0)",
          transition: "all 0.22s ease",
          height: "100%",
        }}
      >
        <div style={{ fontSize: "28px", marginBottom: "12px" }}>{icon}</div>
        <h3 style={{ fontWeight: 700, fontSize: "16px", color: "#12071a", marginBottom: "8px" }}>
          {title}
        </h3>
        <p style={{ fontSize: "14px", color: "#6b6078", lineHeight: 1.65 }}>{desc}</p>
      </div>
    </Reveal>
  );
}

// ── Testimonial card ───────────────────────────────────────────
function TestimonialCard({ quote, name, role, initials, delay }) {
  return (
    <Reveal delay={delay}>
      <div style={{
        backgroundColor: "#fff",
        border: "1.5px solid #f0e8ef",
        borderRadius: "16px",
        padding: "28px 26px",
        height: "100%",
      }}>
        <div style={{ color: "#f59e0b", fontSize: "15px", marginBottom: "14px" }}>★★★★★</div>
        <p style={{
          fontSize: "15px",
          lineHeight: 1.7,
          color: "#2d1f33",
          fontStyle: "italic",
          marginBottom: "20px",
        }}>
          "{quote}"
        </p>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div style={{
            width: 38, height: 38, borderRadius: "50%",
            backgroundColor: `${PURPLE}22`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontWeight: 800, fontSize: "13px", color: PURPLE, flexShrink: 0,
          }}>
            {initials}
          </div>
          <div>
            <p style={{ fontWeight: 700, fontSize: "14px", color: "#12071a" }}>{name}</p>
            <p style={{ fontSize: "12px", color: "#9087a0" }}>{role}</p>
          </div>
        </div>
      </div>
    </Reveal>
  );
}

// ── Main component ─────────────────────────────────────────────
const NepetoLandingPage = () => {
  const navigate    = useNavigate();
  const { isAuthenticated, isLoading, loginWithRedirect, user } = useAuth0();
  const [openFaq, setOpenFaq] = useState(null);
  const [headerScrolled, setHeaderScrolled] = useState(false);
  const [isMonthly, setIsMonthly] = useState(true); // true = monthly shown by default

  const getCheckoutUrl = (plan) => {
    const prd_id = isMonthly ? plan.prd_id_monthly : plan.prd_id_annually;
    const coupon = isMonthly
      ? (plan.monthly === 99 ? "&coupon-code-to-add=MASTER" : "")
      : "&coupon-code-to-add=NEPETOANNUAL";
    const email = user?.email ? `&billing-email=${encodeURIComponent(user.email)}` : "";
    return `https://store.payproglobal.com/checkout?products[1][id]=${prd_id}&page-template=19869&language=en&currency=USD${email}${coupon}`;
  };

  const handlePlanCTA = (plan) => {
    if (!isLoading && isAuthenticated) {
      window.location.href = getCheckoutUrl(plan);
    } else {
      loginWithRedirect({
        authorizationParams: { screen_hint: "signup" },
        appState: { returnTo: "/pricing?s=1" },
      });
    }
  };

  useEffect(() => {
    const onScroll = () => setHeaderScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleCTA = () => {
    if (!isLoading && isAuthenticated) {
      navigate("/pricing");
    } else {
      loginWithRedirect({
        authorizationParams: { screen_hint: "signup" },
        appState: { returnTo: "/pricing?s=1" },
      });
    }
  };

  const features = [
    { icon: "🤖", title: "AI-Powered Discovery", desc: "Our algorithm scans hundreds of retail and wholesale suppliers around the clock, surfacing the highest-margin leads automatically." },
    { icon: "📊", title: "Deep Profit Analytics", desc: "See estimated profit, ROI %, Amazon fees, and sales velocity for every product before committing a single dollar." },
    { icon: "🛍️", title: "Amazon & Walmart", desc: "Full sourcing coverage for FBA, FBM, and Walmart.com sellers — all from one unified dashboard." },
    { icon: "📱", title: "Mobile App & Chrome Extension", desc: "Scan barcodes in-store with the Nepeto mobile app, or use the Chrome extension while browsing. Both are completely free with your plan." },
    { icon: "🔄", title: "Amazon-to-Amazon Flips", desc: "Find products listed cheaply by other Amazon sellers and flip them at a higher price — no external supplier required." },
    { icon: "📦", title: "Out-of-Stock Opportunities", desc: "Discover products currently out of stock on Amazon and see exactly which suppliers carry them, so you can restock first and capture the demand." },
    { icon: "🔍", title: "Master Search", desc: "Drop a Keepa export CSV and instantly find the best supplier for every ASIN in your list. Turn your research into a ready-to-buy sourcing list in seconds." },
    { icon: "🏪", title: "Storefront Scanner", desc: "Reverse-search any Amazon seller's storefront to see what they're profiting from — and where they're buying it. Source the exact same products directly." },
    { icon: "🎓", title: "Free Coaching", desc: "Need help getting started or scaling up? Free coaching is included with every plan — real guidance from people who know the sourcing game inside out." },
  ];

  const testimonials = [
    { quote: "I found 50+ profitable products in my first week. Nepeto completely changed how I source — I'll never go back to manual research.", name: "Nick R.", role: "Amazon FBA Seller", initials: "NR" },
    { quote: "The ROI speaks for itself. I doubled my margins in month one. The AI surfaces deals I would have completely missed on my own.", name: "Joe F.", role: "Amazon & Walmart Seller", initials: "JF" },
    { quote: "It's like having a full-time sourcing assistant that never sleeps. Every morning I wake up to a curated list of fresh, profitable leads.", name: "Mark T.", role: "FBA Business Owner", initials: "MT" },
    { quote: "The storefront scanner alone is worth the subscription. Being able to see what competitors are profiting from is a total game-changer.", name: "Sarah K.", role: "Online Arbitrage Seller", initials: "SK" },
  ];

  const faqs = [
    { q: "What platforms does Nepeto source for?", a: "Nepeto finds profitable products for Amazon (FBA & FBM) and Walmart sellers. We continuously scan hundreds of retail, wholesale, and distributor suppliers to find the best opportunities." },
    { q: "How does the 14-day free trial work?", a: "Sign up and get full access for 14 days — no credit card required. Explore every feature completely free. Cancel before the trial ends and you won't be charged." },
    { q: "What does 44% off annual plans mean?", a: "Choosing annual billing saves you 44% vs. the monthly rate. For example, the Advanced plan drops from $78/mo to $62/mo — saving you over $190/year." },
    { q: "How is Nepeto different from other sourcing tools?", a: "Most tools require you to do the searching yourself. Nepeto's AI does it for you — automatically scanning suppliers and surfacing only the products with the best margins, rank, and demand." },
    { q: "Do I need selling experience to use Nepeto?", a: "No. Whether you're just starting out or scaling an existing store, Nepeto is intuitive to use. We also provide tutorials, live webinars, and a seller community." },
  ];

  return (
    <div style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif", color: "#12071a", backgroundColor: "#fff" }}>
      <Helmet>
        <title>Nepeto — AI Product Sourcing for Amazon & Walmart Sellers</title>
        <meta name="description" content="Nepeto's AI finds profitable Amazon and Walmart products automatically. 14-day free trial. 44% off annual plans." />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap" rel="stylesheet" />
      </Helmet>

      {/* ── HEADER ─────────────────────────────────────────────── */}
      <header style={{
        position: "sticky", top: 0, zIndex: 100,
        backgroundColor: "#fff",
        borderBottom: headerScrolled ? "1px solid #f0e8ef" : "1px solid transparent",
        boxShadow: headerScrolled ? "0 2px 16px rgba(140,62,124,0.07)" : "none",
        transition: "border-color 0.3s ease, box-shadow 0.3s ease",
        padding: "14px 24px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        maxWidth: "100%",
      }}>
        <div style={{ fontWeight: 900, fontSize: "22px", letterSpacing: "-0.04em", color: PURPLE }}>
          Nepeto.
        </div>
        <PrimaryBtn label="Start Free Trial" onClick={handleCTA} />
      </header>

      {/* ── HERO ───────────────────────────────────────────────── */}
      <section style={{
        background: `radial-gradient(ellipse at 65% 40%, ${PURPLE}0d 0%, transparent 65%), #fff`,
        padding: "96px 24px 100px",
        textAlign: "center",
      }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>

          {/* floating badge */}
          <div style={{
            display: "inline-flex", alignItems: "center", gap: "8px",
            backgroundColor: `${PURPLE}12`,
            border: `1px solid ${PURPLE}25`,
            color: PURPLE, fontWeight: 700, fontSize: "13px",
            padding: "7px 18px", borderRadius: "99px",
            marginBottom: "28px",
            animation: "floatBadge 3s ease-in-out infinite",
          }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: PURPLE, display: "inline-block", flexShrink: 0 }} />
            AI-Powered Product Sourcing
          </div>

          <h1 style={{
            fontSize: "clamp(36px, 6vw, 64px)",
            fontWeight: 900,
            letterSpacing: "-0.04em",
            lineHeight: 1.1,
            color: "#12071a",
            marginBottom: "22px",
          }}>
            Stop Hunting for Products.{" "}
            <span style={{ color: PURPLE }}>Let AI Hunt For You.</span>
          </h1>

          <p style={{
            fontSize: "18px",
            lineHeight: 1.7,
            color: "#5a5068",
            marginBottom: "38px",
            maxWidth: 540,
            margin: "0 auto 38px",
          }}>
            Nepeto scans hundreds of suppliers and delivers the most profitable
            Amazon and Walmart leads to your dashboard — every single day.
          </p>

          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "12px" }}>
            <PrimaryBtn onClick={handleCTA} />
            <p style={{ fontSize: "13px", color: "#9087a0", fontWeight: 500 }}>
              Free Chrome extension &nbsp;·&nbsp; Cancel anytime &nbsp;·&nbsp; Full access
            </p>
          </div>

          {/* trust stats */}
          <div style={{
            display: "flex", justifyContent: "center",
            flexWrap: "wrap", gap: "10px 32px",
            marginTop: "52px",
            paddingTop: "32px",
            borderTop: "1px solid #f0e8ef",
          }}>
            {[
              { value: "2,000+", label: "Active sellers" },
              { value: "180+",   label: "Suppliers scanned" },
              { value: "63%",   label: "Average ROI" },
            ].map(({ value, label }) => (
              <div key={label} style={{ textAlign: "center" }}>
                <p style={{ fontWeight: 800, fontSize: "22px", color: PURPLE, letterSpacing: "-0.03em" }}>{value}</p>
                <p style={{ fontSize: "13px", color: "#9087a0", fontWeight: 500 }}>{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── VIDEO ─────────────────────────────────────────────── */}
      <Section bg={PINK_BG}>
        <Reveal>
          <div style={{ textAlign: "center", marginBottom: "40px" }}>
            <EyebrowLabel>See it in action</EyebrowLabel>
            <SectionHeading>Watch Nepeto find $500 in profitable products in 60 seconds.</SectionHeading>
            <SubText>See exactly how the software works — before you sign up for anything.</SubText>
          </div>
        </Reveal>
        <Reveal delay={120}>
          <div style={{
            position: "relative",
            paddingBottom: "56.25%",
            height: 0,
            overflow: "hidden",
            borderRadius: "18px",
            boxShadow: "0 20px 56px rgba(140,62,124,0.16)",
            border: "1.5px solid #ede5eb",
          }}>
            <iframe
              src="https://www.youtube.com/embed/iMVy5Sl5YMs"
              title="Nepeto — AI Product Sourcing Demo"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{
                position: "absolute", top: 0, left: 0,
                width: "100%", height: "100%",
                border: "none",
              }}
            />
          </div>
        </Reveal>
      </Section>

      {/* ── PROBLEM ────────────────────────────────────────────── */}
      <Section bg="#fff">
        <Reveal>
          <div style={{ textAlign: "center", marginBottom: "48px" }}>
            <EyebrowLabel>Sound familiar?</EyebrowLabel>
            <SectionHeading>Manual sourcing is costing you time and money.</SectionHeading>
            <SubText>Most sellers get stuck in the same exhausting loop. There's a better way.</SubText>
          </div>
        </Reveal>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "16px" }}>
          {[
            { icon: "⏳", pain: "Spending hours manually searching across dozens of supplier sites" },
            { icon: "😤", pain: "Finding a good deal too late — already out of stock or oversold" },
            { icon: "📉", pain: "Buying products with thin margins that barely cover fees" },
            { icon: "🔁", pain: "Repeating the same tedious research process every single week" },
          ].map(({ icon, pain }, i) => (
            <Reveal key={pain} delay={i * 80}>
              <div style={{
                backgroundColor: PINK_BG,
                border: "1.5px solid #ede5eb",
                borderRadius: "14px",
                padding: "22px 20px",
                display: "flex", gap: "14px", alignItems: "flex-start",
              }}>
                <span style={{ fontSize: "22px", flexShrink: 0 }}>{icon}</span>
                <p style={{ fontSize: "14px", color: "#4a3857", lineHeight: 1.6, fontWeight: 500 }}>{pain}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* ── HOW IT WORKS ───────────────────────────────────────── */}
      <Section bg={PINK_BG}>
        <Reveal>
          <div style={{ textAlign: "center", marginBottom: "56px" }}>
            <EyebrowLabel>How it works</EyebrowLabel>
            <SectionHeading>Profitable in 3 simple steps.</SectionHeading>
            <SubText>From sign-up to your first profitable lead in under 5 minutes.</SubText>
          </div>
        </Reveal>

        <div style={{ position: "relative", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "0" }}>
          {/* connector line (desktop only) */}
          <div style={{
            position: "absolute", top: "36px", left: "calc(16.6% + 24px)", right: "calc(16.6% + 24px)",
            height: "2px",
            background: `linear-gradient(to right, ${PURPLE}40, ${PURPLE}40)`,
            pointerEvents: "none",
          }} />

          {[
            { n: "01", icon: "🔗", title: "Connect Your Account", desc: "Sign up and link your Amazon seller account in seconds. No complex setup needed." },
            { n: "02", icon: "🤖", title: "AI Scans 24/7",        desc: "Our algorithm continuously checks hundreds of suppliers, comparing prices, margins, and demand signals." },
            { n: "03", icon: "💰", title: "Buy & Profit",          desc: "Review your curated leads, filter by ROI or rank, buy with confidence, and scale." },
          ].map(({ n, icon, title, desc }, i) => (
            <Reveal key={n} delay={i * 100}>
              <div style={{ textAlign: "center", padding: "0 28px 0" }}>
                <div style={{
                  width: 56, height: 56,
                  borderRadius: "50%",
                  border: `2px solid ${PURPLE}30`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  margin: "0 auto 20px",
                  position: "relative", zIndex: 1,
                  background: "#fff",
                  boxShadow: `0 0 0 6px ${PINK_BG}`,
                }}>
                  <span style={{ fontSize: "22px" }}>{icon}</span>
                </div>
                <p style={{ fontWeight: 800, fontSize: "11px", color: PURPLE, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "8px" }}>Step {n}</p>
                <h3 style={{ fontWeight: 700, fontSize: "17px", color: "#12071a", marginBottom: "10px" }}>{title}</h3>
                <p style={{ fontSize: "14px", color: "#6b6078", lineHeight: 1.65 }}>{desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* ── STATS BAND ─────────────────────────────────────────── */}
      <section style={{ backgroundColor: PURPLE, padding: "60px 24px" }}>
        <div style={{ maxWidth: 1040, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "32px", textAlign: "center" }}>
          {[
            { value: "$10M+",   label: "In seller profits generated" },
            { value: "180+",    label: "Supplier sources scanned" },
            { value: "2,000+",  label: "Active sellers on Nepeto" },
            { value: "14 Days", label: "Free trial, no card needed" },
          ].map(({ value, label }, i) => (
            <Reveal key={label} delay={i * 90}>
              <p style={{ fontWeight: 900, fontSize: "clamp(28px, 4vw, 42px)", color: "#fff", letterSpacing: "-0.04em", marginBottom: "6px" }}>{value}</p>
              <p style={{ fontSize: "14px", color: "rgba(255,255,255,0.65)", fontWeight: 500 }}>{label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ── FEATURES ───────────────────────────────────────────── */}
      <Section bg="#fff">
        <Reveal>
          <div style={{ textAlign: "center", marginBottom: "48px" }}>
            <EyebrowLabel>Everything you need</EyebrowLabel>
            <SectionHeading>Built for serious sellers.</SectionHeading>
            <SubText>Every tool you need to source smarter, move faster, and profit more.</SubText>
          </div>
        </Reveal>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "16px" }}>
          {features.map(({ icon, title, desc }, i) => (
            <FeatureCard key={title} icon={icon} title={title} desc={desc} delay={i * 70} />
          ))}
        </div>

        {/* ── GATED / UNGATED SPOTLIGHT ─────────────────────── */}
        <Reveal delay={80}>
          <div style={{
            marginTop: "28px",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: "0",
            backgroundColor: PINK_BG,
            border: `1.5px solid ${PURPLE}20`,
            borderRadius: "20px",
            overflow: "hidden",
          }}>
            {/* Left: copy */}
            <div style={{ padding: "40px 36px" }}>
              <div style={{
                display: "inline-flex", alignItems: "center", gap: "6px",
                backgroundColor: `${PURPLE}12`, border: `1px solid ${PURPLE}22`,
                color: PURPLE, fontWeight: 700, fontSize: "11px",
                padding: "5px 12px", borderRadius: "99px",
                letterSpacing: "0.08em", textTransform: "uppercase",
                marginBottom: "18px",
              }}>
                ✦ Unique to Nepeto
              </div>
              <h3 style={{
                fontWeight: 800, fontSize: "clamp(20px, 3vw, 26px)",
                letterSpacing: "-0.03em", lineHeight: 1.2,
                color: "#12071a", marginBottom: "14px",
              }}>
                Only see products<br />you can actually sell.
              </h3>
              <p style={{ fontSize: "15px", lineHeight: 1.7, color: "#5a5068", marginBottom: "24px" }}>
                Connect your Amazon Seller account once and Nepeto instantly knows
                which categories you're approved for. Every lead you see is one you
                can list — no restricted ASINs cluttering your feed, no wasted clicks.
                It's one of the fastest sourcing experiences on the market.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: "11px" }}>
                {[
                  "Gated & restricted products automatically filtered out",
                  "Approval-aware leads from the moment you connect",
                  "Zero time wasted on products you can't list",
                ].map(item => (
                  <div key={item} style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
                    <div style={{
                      width: 20, height: 20, borderRadius: "50%",
                      backgroundColor: `${PURPLE}15`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      flexShrink: 0, marginTop: "2px",
                    }}>
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                        <path d="M1 4L3.5 6.5L9 1" stroke={PURPLE} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    <p style={{ fontSize: "14px", color: "#4a3857", fontWeight: 500, lineHeight: 1.55 }}>{item}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: visual callout */}
            <div style={{
              backgroundColor: `${PURPLE}08`,
              borderLeft: `1.5px solid ${PURPLE}15`,
              padding: "40px 36px",
              display: "flex", flexDirection: "column", justifyContent: "center", gap: "16px",
            }}>
              <div style={{ fontSize: "44px", textAlign: "center" }}>🔓</div>
              <p style={{
                fontWeight: 800, fontSize: "17px", color: "#12071a",
                textAlign: "center", lineHeight: 1.3, marginBottom: "4px",
              }}>
                Your account.<br />Your approvals.
              </p>
              <p style={{ fontSize: "14px", color: "#6b6078", lineHeight: 1.65, textAlign: "center" }}>
                Connect once and Nepeto permanently tailors every lead to what <em>you</em> are approved to sell — not what everyone else can.
              </p>
              <div style={{
                backgroundColor: "#fff",
                border: `1px solid ${PURPLE}18`,
                borderRadius: "12px",
                padding: "16px 18px",
                marginTop: "8px",
              }}>
                <p style={{ fontSize: "13px", fontWeight: 600, color: PURPLE, fontStyle: "italic", lineHeight: 1.6, textAlign: "center" }}>
                  "I stopped wasting time on restricted products the day I connected my account. It's a total game-changer."
                </p>
                <p style={{ fontSize: "12px", color: "#9087a0", textAlign: "center", marginTop: "8px", fontWeight: 600 }}>
                  — David M., FBA Seller
                </p>
              </div>
            </div>
          </div>
        </Reveal>
      </Section>

      {/* ── TESTIMONIALS ───────────────────────────────────────── */}
      <Section bg={PINK_BG}>
        <Reveal>
          <div style={{ textAlign: "center", marginBottom: "48px" }}>
            <EyebrowLabel>What sellers say</EyebrowLabel>
            <SectionHeading>Sellers like you are already winning.</SectionHeading>
            <SubText>Join thousands of Amazon and Walmart sellers who automated their sourcing with Nepeto.</SubText>
          </div>
        </Reveal>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "16px" }}>
          {testimonials.map(({ quote, name, role, initials }, i) => (
            <TestimonialCard key={name} quote={quote} name={name} role={role} initials={initials} delay={i * 80} />
          ))}
        </div>
      </Section>

      {/* ── PRICING ────────────────────────────────────────────── */}
      <Section bg="#fff">
        <Reveal>
          <div style={{ textAlign: "center", marginBottom: "36px" }}>
            <EyebrowLabel>Pricing</EyebrowLabel>
            <SectionHeading>Simple, transparent pricing.</SectionHeading>
            <SubText>Start free. Upgrade when you're ready. Cancel anytime.</SubText>
          </div>
        </Reveal>

        {/* Billing toggle */}
        <Reveal delay={60}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: "40px" }}>
            <div style={{
              display: "inline-flex", alignItems: "center",
              backgroundColor: PINK_BG, borderRadius: "99px",
              padding: "5px", border: "1.5px solid #ede5eb",
            }}>
              <button
                onClick={() => setIsMonthly(true)}
                style={{
                  padding: "9px 24px", borderRadius: "99px", border: "none", cursor: "pointer",
                  backgroundColor: isMonthly ? "#fff" : "transparent",
                  boxShadow: isMonthly ? "0 1px 6px rgba(0,0,0,0.10)" : "none",
                  fontWeight: isMonthly ? 700 : 500, fontSize: "14px",
                  color: isMonthly ? "#12071a" : "#9087a0",
                  transition: "all 0.2s ease",
                }}
              >
                Monthly
              </button>
              <button
                onClick={() => setIsMonthly(false)}
                style={{
                  padding: "9px 24px", borderRadius: "99px", border: "none", cursor: "pointer",
                  backgroundColor: !isMonthly ? "#fff" : "transparent",
                  boxShadow: !isMonthly ? "0 1px 6px rgba(0,0,0,0.10)" : "none",
                  fontWeight: !isMonthly ? 700 : 500, fontSize: "14px",
                  color: !isMonthly ? "#12071a" : "#9087a0",
                  transition: "all 0.2s ease",
                  display: "flex", alignItems: "center", gap: "8px",
                }}
              >
                Annual
                <span style={{
                  backgroundColor: "#d1fae5", color: "#065f46",
                  fontSize: "10px", fontWeight: 800,
                  padding: "2px 8px", borderRadius: "99px",
                }}>
                  44% OFF
                </span>
              </button>
            </div>
          </div>
        </Reveal>

        {/* Plan cards */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "16px", alignItems: "start" }}>
          {[
            {
              name: "Starter", monthly: 34, annual: 29, highlight: false,
              prd_id_monthly: "105675", prd_id_annually: "105673",
              trial: "7-day free trial",
              features: [
                "Retail supplier products (Walmart, Target, etc.)",
                "Products you're approved to sell",
                "Mobile App — 2 installs",
                "Chrome Extension — 2 installs",
                "Storefront Scanner — 3/day",
              ],
            },
            {
              name: "Advanced", monthly: 78, annual: 62, highlight: true,
              prd_id_monthly: "101488", prd_id_annually: "101494",
              trial: "14-day free trial",
              features: [
                "Everything in Starter",
                "Wholesale & distributor products",
                "Out-of-stock Amazon opportunities",
                "Amazon-to-Amazon flips",
                "Advanced product data",
                "Storefront Scanner — 6/day",
              ],
            },
            {
              name: "Pro", monthly: 98, annual: 78, highlight: false,
              prd_id_monthly: "101490", prd_id_annually: "101497",
              trial: "14-day free trial",
              features: [
                "Everything in Advanced",
                "Amazon → Walmart sourcing",
                "Pro data & advanced filters",
                "Filter by profit & sort by found time",
                "Export deals to CSV",
                "Storefront Scanner — 15/day",
              ],
            },
            {
              name: "Master", monthly: 99, annual: 83, highlight: false,
              prd_id_monthly: "101486", prd_id_annually: "101498",
              trial: "14-day free trial",
              features: [
                "Everything in Pro",
                "CSV Scanner (Keepa export → suppliers)",
                "Nepeto Master Search",
                "Nepeto LIVE Search",
                "RA Scanning App + Profit Calculator",
                "Storefront Scanner — 30/day",
                "👑 Nepeto Mastermind",
              ],
            },
          ].map((plan, i) => {
            const price    = isMonthly ? plan.monthly : (plan.annual * 0.7).toFixed(2);
            const savings  = Math.round((1 - (plan.annual * 0.7) / plan.monthly) * 100);
            return (
              <Reveal key={plan.name} delay={i * 80}>
                <div style={{
                  position: "relative",
                  backgroundColor: plan.highlight ? PURPLE : "#fff",
                  border: plan.highlight ? `2px solid ${PURPLE}` : "1.5px solid #f0e8ef",
                  borderRadius: "20px",
                  padding: "32px 24px 28px",
                  boxShadow: plan.highlight ? `0 16px 40px ${PURPLE}30` : "0 2px 8px rgba(0,0,0,0.04)",
                  transform: plan.highlight ? "scale(1.03)" : "scale(1)",
                  display: "flex", flexDirection: "column",
                }}>
                  {plan.highlight && (
                    <div style={{
                      position: "absolute", top: "-13px", left: "50%", transform: "translateX(-50%)",
                      backgroundColor: ORANGE, color: "#fff", fontSize: "10px",
                      fontWeight: 800, padding: "4px 14px", borderRadius: "99px",
                      letterSpacing: "0.06em", textTransform: "uppercase", whiteSpace: "nowrap",
                    }}>
                      Most Popular
                    </div>
                  )}

                  {/* Plan name */}
                  <p style={{
                    fontWeight: 800, fontSize: "13px", letterSpacing: "0.08em",
                    color: plan.highlight ? "rgba(255,255,255,0.65)" : "#9087a0",
                    marginBottom: "12px", textTransform: "uppercase",
                  }}>
                    {plan.name}
                  </p>

                  {/* Price */}
                  <div style={{ marginBottom: "4px", display: "flex", alignItems: "baseline", gap: "6px" }}>
                    {!isMonthly && (
                      <span style={{
                        fontSize: "14px",
                        color: plan.highlight ? "rgba(255,255,255,0.4)" : "#c4b8cc",
                        textDecoration: "line-through",
                      }}>
                        ${plan.annual}
                      </span>
                    )}
                    <span style={{
                      fontSize: "40px", fontWeight: 900, letterSpacing: "-0.04em",
                      color: plan.highlight ? "#fff" : "#12071a", lineHeight: 1,
                    }}>
                      ${price}
                    </span>
                    <span style={{ fontSize: "14px", color: plan.highlight ? "rgba(255,255,255,0.55)" : "#9087a0" }}>/mo</span>
                  </div>

                  <p style={{ fontSize: "12px", color: plan.highlight ? "rgba(255,255,255,0.45)" : "#b0a0b8", marginBottom: "8px" }}>
                    {isMonthly ? "billed monthly" : `$${(plan.annual * 0.7 * 12).toFixed(2)} billed annually`}
                  </p>

                  {/* Savings pill — annual only */}
                  {!isMonthly ? (
                    <div style={{
                      display: "inline-block", alignSelf: "flex-start", marginBottom: "16px",
                      backgroundColor: plan.highlight ? "rgba(255,255,255,0.15)" : "#d1fae5",
                      borderRadius: "99px", padding: "3px 10px",
                      fontSize: "11px", fontWeight: 700,
                      color: plan.highlight ? "#fff" : "#065f46",
                    }}>
                      Save {savings}% vs monthly
                    </div>
                  ) : <div style={{ marginBottom: "16px" }} />}

                  {/* Trial badge */}
                  <div style={{
                    backgroundColor: plan.highlight ? "rgba(255,255,255,0.12)" : PINK_BG,
                    border: `1px solid ${plan.highlight ? "rgba(255,255,255,0.2)" : "#ede5eb"}`,
                    borderRadius: "8px", padding: "7px 12px",
                    fontSize: "12px", fontWeight: 700,
                    color: plan.highlight ? "rgba(255,255,255,0.9)" : PURPLE,
                    marginBottom: "20px", textAlign: "center",
                  }}>
                    ✦ {plan.trial}
                  </div>

                  {/* Feature list */}
                  <div style={{ display: "flex", flexDirection: "column", gap: "9px", marginBottom: "24px", flex: 1 }}>
                    {plan.features.map(feat => (
                      <div key={feat} style={{ display: "flex", gap: "9px", alignItems: "flex-start" }}>
                        <div style={{
                          width: 16, height: 16, borderRadius: "50%", flexShrink: 0, marginTop: "2px",
                          backgroundColor: plan.highlight ? "rgba(255,255,255,0.2)" : `${PURPLE}15`,
                          display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                          <svg width="8" height="7" viewBox="0 0 8 7" fill="none">
                            <path d="M1 3.5L3 5.5L7 1" stroke={plan.highlight ? "#fff" : PURPLE} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </div>
                        <p style={{ fontSize: "13px", lineHeight: 1.5, color: plan.highlight ? "rgba(255,255,255,0.82)" : "#4a3857", fontWeight: 500 }}>
                          {feat}
                        </p>
                      </div>
                    ))}
                  </div>

                  {/* CTA */}
                  <button
                    onClick={() => handlePlanCTA(plan)}
                    onMouseEnter={e => e.currentTarget.style.opacity = "0.88"}
                    onMouseLeave={e => e.currentTarget.style.opacity = "1"}
                    style={{
                      width: "100%", padding: "13px",
                      borderRadius: "12px", border: "none", cursor: "pointer",
                      fontWeight: 800, fontSize: "14px",
                      backgroundColor: plan.highlight ? "#fff" : PURPLE,
                      color: plan.highlight ? PURPLE : "#fff",
                      boxShadow: plan.highlight ? "0 4px 14px rgba(255,255,255,0.2)" : `0 4px 14px ${PURPLE}30`,
                      transition: "opacity 0.2s ease",
                    }}
                  >
                    Get Started →
                  </button>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={100}>
          <p style={{ textAlign: "center", marginTop: "20px", fontSize: "13px", color: "#9087a0" }}>
            All plans include a free trial. Subscriptions can be cancelled anytime.
          </p>
        </Reveal>
      </Section>

      {/* ── FAQ ────────────────────────────────────────────────── */}
      <Section bg={PINK_BG}>
        <Reveal>
          <div style={{ textAlign: "center", marginBottom: "48px" }}>
            <EyebrowLabel>FAQ</EyebrowLabel>
            <SectionHeading>Common questions.</SectionHeading>
          </div>
        </Reveal>
        <div style={{ maxWidth: 680, margin: "0 auto", display: "flex", flexDirection: "column", gap: "10px" }}>
          {faqs.map(({ q, a }, i) => (
            <Reveal key={i} delay={i * 60}>
              <div
                style={{
                  backgroundColor: "#fff",
                  border: "1.5px solid #f0e8ef",
                  borderRadius: "14px",
                  overflow: "hidden",
                }}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  style={{
                    width: "100%", textAlign: "left",
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    padding: "18px 22px", gap: "16px",
                    fontWeight: 600, fontSize: "15px", color: "#12071a",
                    background: "none", border: "none", cursor: "pointer",
                  }}
                >
                  <span>{q}</span>
                  <span style={{
                    color: PURPLE, fontSize: "22px", fontWeight: 300,
                    transform: openFaq === i ? "rotate(45deg)" : "rotate(0deg)",
                    transition: "transform 0.22s ease",
                    flexShrink: 0, lineHeight: 1,
                  }}>
                    +
                  </span>
                </button>
                {openFaq === i && (
                  <div style={{ padding: "0 22px 18px", fontSize: "14px", color: "#6b6078", lineHeight: 1.7, borderTop: "1px solid #f7f0f5" }}>
                    <div style={{ paddingTop: "14px" }}>{a}</div>
                  </div>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* ── FINAL CTA ──────────────────────────────────────────── */}
      <section style={{ backgroundColor: PURPLE_DARK, padding: "96px 24px", textAlign: "center" }}>
        <div style={{ maxWidth: 600, margin: "0 auto" }}>
          <Reveal>
            <p style={{ color: `${PURPLE}99`, fontWeight: 700, fontSize: "11px", letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(255,255,255,0.45)", marginBottom: "16px" }}>
              Ready to scale your store?
            </p>
            <h2 style={{ fontSize: "clamp(30px, 5vw, 50px)", fontWeight: 900, letterSpacing: "-0.04em", lineHeight: 1.12, color: "#fff", marginBottom: "18px" }}>
              Source smarter.<br />Profit faster.
            </h2>
            <p style={{ fontSize: "17px", lineHeight: 1.7, color: "rgba(255,255,255,0.62)", marginBottom: "36px", maxWidth: 440, margin: "0 auto 36px" }}>
              Join 2,000+ sellers who use Nepeto to automate their product sourcing and grow their margins every month.
            </p>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "12px" }}>
              <PrimaryBtn onClick={handleCTA} />
              <p style={{ fontSize: "13px", color: "rgba(255,255,255,0.4)", fontWeight: 500 }}>
                No credit card required &nbsp;·&nbsp; 44% off annual plans
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── FOOTER ─────────────────────────────────────────────── */}
      <footer style={{ backgroundColor: "#0d0614", padding: "28px 24px", textAlign: "center" }}>
        <p style={{ fontSize: "13px", color: "#4a3a54" }}>
          © {new Date().getFullYear()} Nepeto. All rights reserved.
          {" · "}
        </p>
      </footer>

      {/* ── KEYFRAMES ──────────────────────────────────────────── */}
      <style>{`
        @keyframes floatBadge {
          0%, 100% { transform: translateY(0px);  }
          50%       { transform: translateY(-5px); }
        }
      `}</style>
    </div>
  );
};

export default NepetoLandingPage;
